import { useEffect, useState } from "react";
import { Heart } from "lucide-react";

const SESSION_KEY = "ouder_splash_shown";
const AUTO_DISMISS_MS = 2200;
const FADE_MS = 500;

/** Warme oranje splash die bij PWA-start één keer per sessie kort verschijnt.
 *  Toont foto van het geselecteerde kind (of initialen fallback) met persoonlijke
 *  begroeting op basis van het uur — voelt bij openen direct persoonlijk.
 *
 *  Props:
 *  - kinderen: [{ id, voornaam }]  (verplicht)
 *  - fotoUrl: string | null        (recentste foto — optioneel)
 *  - ouderNaam: string              (voornaam ouder)
 */
export default function OuderSplash({ kinderen = [], ouderNaam }) {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    // Alleen tonen bij PWA-start én één keer per sessie.
    // Belangrijk: in React StrictMode wordt dit effect twee keer opgeroepen. De
    // sessionStorage-marker moet daarom PAS gezet worden nádat de dismiss-timer
    // is voltooid — anders blijft de overlay hangen bij de 2e mount (cleanup
    // wist de timers, en de 2e run stopt vroeg omdat de marker al bestaat).
    let cancelled = false;
    try {
      const already = sessionStorage.getItem(SESSION_KEY);
      if (already) return;
      if (!kinderen || kinderen.length === 0) return;
      setVisible(true);
      const t1 = setTimeout(() => { if (!cancelled) setFading(true); }, AUTO_DISMISS_MS - FADE_MS);
      const t2 = setTimeout(() => {
        if (cancelled) return;
        setVisible(false);
        try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {}
      }, AUTO_DISMISS_MS);
      return () => { cancelled = true; clearTimeout(t1); clearTimeout(t2); };
    } catch { /* sessionStorage niet beschikbaar */ }
  }, [kinderen]);

  if (!visible) return null;

  const kind = kinderen[0] || {};
  const naam = kind.voornaam || "";
  const initials = (naam.slice(0, 1) + (kind.achternaam?.slice(0, 1) || "")).toUpperCase();
  const uur = new Date().getHours();
  const groet = uur < 6 ? "Goedenacht" : uur < 12 ? "Goedemorgen" : uur < 18 ? "Goedemiddag" : "Goedenavond";
  const ouderStuk = ouderNaam ? `, ${ouderNaam.split(" ")[0]}` : "";

  return (
    <div
      onClick={() => { setFading(true); setTimeout(() => setVisible(false), FADE_MS); }}
      role="dialog"
      aria-label="Welkom-scherm ouder"
      data-testid="ouder-splash"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "linear-gradient(135deg, #c9612e 0%, #d97742 45%, #e89a5c 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: 24,
        opacity: fading ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
        cursor: "pointer",
        color: "white",
        padding: 24,
      }}
    >
      <style>{`
        @keyframes ouder-splash-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.04); } }
        @keyframes ouder-splash-rise { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      {/* Kind-initialen in warme ring */}
      <div style={{
        width: 168, height: 168, borderRadius: "50%",
        background: "rgba(255,255,255,0.15)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25), inset 0 0 0 4px rgba(255,255,255,0.4)",
        animation: "ouder-splash-pulse 2.4s ease-in-out infinite",
      }}>
        <div style={{
          width: 128, height: 128, borderRadius: "50%",
          background: "rgba(255,255,255,0.95)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#c9612e", fontSize: 44, fontWeight: 700, letterSpacing: -1,
          position: "relative",
        }}>
          {initials || <Heart className="h-12 w-12" />}
          {/* Klein hartje-badge rechtsonder voor warmte */}
          <div style={{
            position: "absolute", right: -4, bottom: -4,
            width: 36, height: 36, borderRadius: "50%",
            background: "#c9612e",
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "3px solid white",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          }}>
            <Heart className="h-4 w-4 text-white" fill="white" />
          </div>
        </div>
      </div>

      <div style={{ textAlign: "center", animation: "ouder-splash-rise 500ms ease 200ms both" }}>
        <div style={{ fontSize: 15, opacity: 0.85, letterSpacing: 0.5, fontWeight: 500 }}>
          {groet}{ouderStuk}
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, letterSpacing: -0.5 }}>
          Welkom terug
        </div>
        {kinderen.length > 0 && (
          <div style={{ fontSize: 15, opacity: 0.9, marginTop: 8 }}>
            {kinderen.length === 1
              ? <>Klaar om te zien hoe het gaat met <strong>{naam}</strong> 💛</>
              : <>Klaar om te zien hoe het gaat met <strong>{kinderen.map(k => k.voornaam).join(" & ")}</strong> 💛</>}
          </div>
        )}
      </div>

      <div style={{ position: "absolute", bottom: 32, fontSize: 11, opacity: 0.7 }}>
        Tik om over te slaan
      </div>
    </div>
  );
}
